import { useEffect, useState } from 'react';
import { pdfjs } from 'react-pdf';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { pdfOptions } from '@/config/pdf-options';
import { ptToPx, Size } from './geometry';

/** Where a load failed: reading the file, pdf.js opening it, or reading its pages. */
export type PdfLoadStage = 'read' | 'open' | 'pages';

export type PdfDocumentState = {
	doc: PDFDocumentProxy | null;
	/** Page sizes in base px (96 dpi), rotation applied. */
	pageSizes: Size[];
	isLoading: boolean;
	error: unknown;
	errorStage?: PdfLoadStage;
};

const EMPTY_STATE: PdfDocumentState = {
	doc: null,
	pageSizes: [],
	isLoading: false,
	error: null,
};

/**
 * Loads `file` once with pdf.js (worker configured in src/index.tsx through
 * react-pdf's pdfjs export) and reads every page's size up front so the whole
 * document can be laid out before any page is rendered.
 */
export function usePdfDocument(
	file: Blob | null | undefined,
): PdfDocumentState {
	const [state, setState] = useState<PdfDocumentState>(EMPTY_STATE);

	useEffect(() => {
		if (!file) {
			setState(EMPTY_STATE);
			return;
		}
		let isCancelled = false;
		let loadedDoc: PDFDocumentProxy | null = null;
		setState({ ...EMPTY_STATE, isLoading: true });
		let stage: PdfLoadStage = 'read';

		const loadingTask = (async () => {
			const data = new Uint8Array(await file.arrayBuffer());
			if (isCancelled) return null;
			stage = 'open';
			return pdfjs.getDocument({ ...pdfOptions, data });
		})();

		loadingTask
			.then(async (task) => {
				if (!task) return;
				if (isCancelled) {
					task.destroy();
					return;
				}
				const doc = (await task.promise) as unknown as PDFDocumentProxy;
				loadedDoc = doc;
				stage = 'pages';
				const pages = await Promise.all(
					Array.from({ length: doc.numPages }, (_, index) =>
						doc.getPage(index + 1),
					),
				);
				const pageSizes = pages.map((page) => {
					const viewport = page.getViewport({ scale: 1 });
					return {
						width: ptToPx(viewport.width),
						height: ptToPx(viewport.height),
					};
				});
				if (!isCancelled) {
					setState({ doc, pageSizes, isLoading: false, error: null });
				}
			})
			.catch((error) => {
				if (!isCancelled) {
					setState({ ...EMPTY_STATE, error, errorStage: stage });
				}
			});

		return () => {
			isCancelled = true;
			loadedDoc?.destroy();
			loadingTask.then((task) => task?.destroy()).catch(() => undefined);
		};
	}, [file]);

	return state;
}
